import React, { useState } from 'react'
import { BookingList, type OfferBooking } from './BookingSummary'
import { Card, Eyebrow, day, fieldCls, money, primaryCls, secondaryCls } from './ui'

type Props = {
  deal: any
  busy: boolean
  onAccept: () => void
  onPass: () => void
  onCounter: (amount: number, note: string) => Promise<unknown>
  onQuote: (amount: number, note: string) => Promise<unknown>
}

/**
 * The first stage of a deal: the brand's offer (accept, counter or pass), or its request for
 * the athlete's rate, or the wait while the brand decides on the athlete's pitch or counter.
 */
export const AgreementCard: React.FC<Props> = ({
  deal: d,
  busy,
  onAccept,
  onPass,
  onCounter,
  onQuote,
}) => {
  const [mode, setMode] = useState<'idle' | 'counter'>('idle')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const brand = d.brand?.name ?? 'The brand'
  const booking = (d.booking ?? d.offerDetails?.booking) as OfferBooking | undefined
  // Everything in the offer: a brand's cart can send several items at once
  const bookings = (
    d.bookings?.length ? d.bookings : (d.offerDetails?.bookings ?? (booking ? [booking] : []))
  ) as OfferBooking[]
  const details = d.offerDetails ?? {}
  const due = d.contentDueDate ? ` · due ${day(d.contentDueDate)}` : ''

  // The brand wants a price from the athlete on a custom request
  const askedForRate = d.status === 'offered' && details.rate_requested && !details.quote

  if (d.status === 'offered' && askedForRate) {
    return (
      <Card tone="lime">
        <Eyebrow tone="lime">Name your rate</Eyebrow>
        <p className="mt-1 text-sm text-foreground">
          {brand} wants your price for this{due}
        </p>
        <BookingList bookings={bookings} />
        {details.message ? (
          <p className="mt-2 text-sm text-muted-foreground">“{details.message}”</p>
        ) : null}
        <AmountForm
          busy={busy}
          amount={amount}
          note={note}
          setAmount={setAmount}
          setNote={setNote}
          button="Send my rate"
          onSubmit={() => onQuote(Number(amount), note.trim())}
        />
        <button type="button" className={`mt-2 ${secondaryCls}`} disabled={busy} onClick={onPass}>
          Pass
        </button>
      </Card>
    )
  }

  if (d.status === 'offered') {
    const countered = details.countered_by === 'brand' && details.counter
    return (
      <Card tone="lime">
        <Eyebrow tone="lime">{countered ? `${brand} countered` : 'Offer for you'}</Eyebrow>
        <p className="mt-1 text-sm text-foreground">
          {brand} {countered ? 'now offers' : 'offered'}{' '}
          <span className="font-semibold">{money(d.paymentAmount)}</span>
          {countered && details.counter.was ? (
            <span className="text-muted-foreground">
              {' '}
              (you asked {money(details.counter.was)})
            </span>
          ) : null}
          {due}
        </p>
        <BookingList bookings={bookings} />
        {details.brand_response || details.message ? (
          <p className="mt-2 text-sm text-muted-foreground">
            “{details.brand_response ?? details.message}”
          </p>
        ) : null}
        {mode === 'idle' ? (
          <div className="mt-3 grid grid-cols-3 gap-2">
            <button type="button" className={primaryCls} disabled={busy} onClick={onAccept}>
              Accept
            </button>
            <button type="button" className={secondaryCls} onClick={() => setMode('counter')}>
              Counter
            </button>
            <button type="button" className={secondaryCls} disabled={busy} onClick={onPass}>
              Pass
            </button>
          </div>
        ) : (
          <>
            <AmountForm
              busy={busy}
              amount={amount}
              note={note}
              setAmount={setAmount}
              setNote={setNote}
              placeholder={`Your price (they offered ${money(d.paymentAmount)})`}
              button="Send counter"
              onSubmit={async () => {
                await onCounter(Number(amount), note.trim())
                setMode('idle')
              }}
            />
            <button
              type="button"
              className="mt-2 w-full text-center text-sm font-medium text-muted-foreground"
              onClick={() => setMode('idle')}
            >
              Never mind
            </button>
          </>
        )}
      </Card>
    )
  }

  if (d.status === 'pending_brand') {
    const what =
      details.counter?.at && details.countered_by === 'athlete'
        ? `Your counter of ${money(d.paymentAmount)}`
        : details.quote
          ? `Your rate of ${money(details.quote.amount)}`
          : d.paymentAmount
            ? `Your pitch at ${money(d.paymentAmount)}`
            : 'Your pitch'
    return (
      <Card>
        <Eyebrow>Waiting on the brand</Eyebrow>
        <p className="mt-1 text-sm text-foreground">
          {what} is with {brand}. They can accept, counter or pass. We'll let you know.
        </p>
      </Card>
    )
  }

  const ended = {
    rejected: ['Declined', 'This deal didn’t go ahead.'],
    expired: ['Expired', 'This offer closed before it was answered.'],
    withdrawn: ['Withdrawn', `${brand} withdrew this offer, so there’s nothing to answer.`],
  }[d.status as string]
  if (ended) {
    return (
      <Card>
        <Eyebrow>{ended[0]}</Eyebrow>
        <p className="mt-1 text-sm text-muted-foreground">{ended[1]}</p>
      </Card>
    )
  }
  return null
}

const AmountForm: React.FC<{
  busy: boolean
  amount: string
  note: string
  setAmount: (v: string) => void
  setNote: (v: string) => void
  button: string
  placeholder?: string
  onSubmit: () => void
}> = ({ busy, amount, note, setAmount, setNote, button, placeholder, onSubmit }) => (
  <div className="mt-3 space-y-2">
    <div className="relative">
      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-muted-foreground">
        $
      </span>
      <input
        type="number"
        inputMode="numeric"
        min={1}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder={placeholder ?? 'Your price'}
        aria-label="Amount"
        className={`${fieldCls} pl-8`}
      />
    </div>
    <textarea
      value={note}
      onChange={(e) => setNote(e.target.value)}
      placeholder="Anything they should know? (optional)"
      className={`${fieldCls} min-h-20 resize-none`}
    />
    <button
      type="button"
      className={primaryCls}
      disabled={busy || !(Number(amount) > 0)}
      onClick={onSubmit}
    >
      {button}
    </button>
  </div>
)

export default AgreementCard
